"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { CART_QUERY_KEY } from "@repo/constants";
import { FragmentOfShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { lazy, Suspense, type Usable } from "react";
import { useCartId } from "#/hooks/shopify";
import { SlBag } from "react-icons/sl";
import type { ResolvedIntlConfig } from "react-intl";
import { useAnalytics } from "use-analytics";
import { LoadingDots } from "@repo/components/atoms/loading-dots";
import { Price } from "@repo/components/atoms/price";
import { readFragmentShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { BaseCartLineConnectionMasked_Fragment, Cart_Fragment } from "#/graphql/shopify-storefront/fragments";
import { CartQueryOrCartCreateMutationHandler } from "#/graphql/shopify-storefront/handlers";
import { useGetIntl } from "@repo/i18n/intl/client";
import { BaseCartLine } from "./base-cart-line";
const AddPremiumPackaging = lazy(() => import("#/components/molecules/cart/add-premium-packaging"));
export function CartEmpty(t0) {
  const $ = _c(8);
  const {
    dictionaryPromise,
    lang
  } = t0;
  let t1;
  if ($[0] !== dictionaryPromise || $[1] !== lang) {
    t1 = {
      dictionaryPromise,
      lang
    };
    $[0] = dictionaryPromise;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const intl = useGetIntl(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SlBag className="icon fill h-8 w-8" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== intl) {
    t3 = intl.formatMessage({
      defaultMessage: "Your cart is empty",
      description: "Your cart is empty",
      id: "bupPhI"
    });
    $[4] = intl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="grid grid-flow-col gap-4 content-center items-center mx-auto w-fit">{t2}<span className="font-serif">{t3}</span></div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
export function CartForm(t0) {
  const $ = _c(96);
  const {
    cartId: initialCartId,
    container,
    dictionaryPromise,
    initialCartMasked,
    lang
  } = t0;
  let t1;
  if ($[0] !== dictionaryPromise || $[1] !== lang) {
    t1 = {
      dictionaryPromise,
      lang
    };
    $[0] = dictionaryPromise;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const intl = useGetIntl(t1);
  const {
    track
  } = useAnalytics() || {};
  let t2;
  if ($[3] !== container || $[4] !== track) {
    t2 = () => {
      track("begin_checkout", {
        container
      });
    };
    $[3] = container;
    $[4] = track;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleClickTrack = t2;
  const cartId = useCartId(initialCartId);
  const t3 = !!cartId;
  let t4;
  let t5;
  if ($[6] !== cartId) {
    t4 = [CART_QUERY_KEY, cartId];
    t5 = async () => {
      const variables = {
        cartId
      };
      const cartMasked = await CartQueryOrCartCreateMutationHandler({
        variables
      });
      return cartMasked;
    };
    $[6] = cartId;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== initialCartMasked || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = {
      enabled: t3,
      initialData: initialCartMasked,
      queryKey: t4,
      queryFn: t5,
      staleTime: 5000
    };
    $[9] = initialCartMasked;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    data: cartMasked_0,
    isLoading
  } = useQuery(t6);
  let cart;
  let t10;
  let t7;
  let t8;
  let t9;
  if ($[14] !== cartId || $[15] !== cartMasked_0 || $[16] !== dictionaryPromise || $[17] !== isLoading || $[18] !== lang) {
    t10 = Symbol.for("react.early_return_sentinel");
    bb0: {
      cart = readFragmentShopifyStorefront(Cart_Fragment, cartMasked_0);
      if (isLoading && !!cartId && !cartMasked_0) {
        t10 = null;
        break bb0;
      }
      if (!cart || !cartMasked_0) {
        let t11;
        if ($[24] !== dictionaryPromise || $[25] !== lang) {
          t11 = <CartEmpty dictionaryPromise={dictionaryPromise} lang={lang} />;
          $[24] = dictionaryPromise;
          $[25] = lang;
          $[26] = t11;
        } else {
          t11 = $[26];
        }
        t10 = t11;
        break bb0;
      }
      const {
        baseCartLineConnectionMasked
      } = cart;
      const baseCartLinesMasked = readFragmentShopifyStorefront(BaseCartLineConnectionMasked_Fragment, baseCartLineConnectionMasked)?.edges.map(_temp);
      if (!baseCartLinesMasked || baseCartLinesMasked.length === 0) {
        let t11;
        if ($[27] !== dictionaryPromise || $[28] !== lang) {
          t11 = <CartEmpty dictionaryPromise={dictionaryPromise} lang={lang} />;
          $[27] = dictionaryPromise;
          $[28] = lang;
          $[29] = t11;
        } else {
          t11 = $[29];
        }
        t10 = t11;
        break bb0;
      }
      t9 = "grid gap-4 grid-rows-[1fr_auto] @xl/cart:grid-cols-2";
      t7 = "grid gap-2 content-start overflow-auto";
      t8 = baseCartLinesMasked?.map((baseCartLineMasked_0, i) => <li key={i} className="flex w-full flex-col border-neutral-300 dark:border-neutral-700"><BaseCartLine baseCartLineMasked={baseCartLineMasked_0} cartMasked={cartMasked_0} dictionaryPromise={dictionaryPromise} lang={lang} /></li>);
    }
    $[14] = cartId;
    $[15] = cartMasked_0;
    $[16] = dictionaryPromise;
    $[17] = isLoading;
    $[18] = lang;
    $[19] = cart;
    $[20] = t10;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    cart = $[19];
    t10 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  if (t10 !== Symbol.for("react.early_return_sentinel")) {
    return t10;
  }
  let t11;
  if ($[30] !== t7 || $[31] !== t8) {
    t11 = <ul className={t7}>{t8}</ul>;
    $[30] = t7;
    $[31] = t8;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <LoadingDots />;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== dictionaryPromise || $[35] !== lang) {
    t13 = <Suspense fallback={t12}><AddPremiumPackaging className="self-end grid-cols-2 bg-background-muted p-4" dictionaryPromise={dictionaryPromise} lang={lang} /></Suspense>;
    $[34] = dictionaryPromise;
    $[35] = lang;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t11 || $[38] !== t13 || $[39] !== t9) {
    t14 = <div className={t9}>{t11}{t13}</div>;
    $[37] = t11;
    $[38] = t13;
    $[39] = t9;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== intl) {
    t15 = intl.formatMessage({
      defaultMessage: "Taxes",
      description: "Taxes",
      id: "Gb2+bH"
    });
    $[41] = intl;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t15) {
    t16 = <span className="uppercase">{t15}</span>;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const t17 = cart?.cost?.totalAmount?.amount;
  const t18 = cart?.cost?.totalAmount?.currencyCode;
  let t19;
  if ($[45] !== lang || $[46] !== t17 || $[47] !== t18) {
    t19 = <Price amount={t17} className="text-right" currencyCode={t18} lang={lang} />;
    $[45] = lang;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t16 || $[50] !== t19) {
    t20 = <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1 dark:border-neutral-700">{t16}{t19}</div>;
    $[49] = t16;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== intl) {
    t21 = intl.formatMessage({
      defaultMessage: "Total Items",
      description: "Total Items",
      id: "cT/XOj"
    });
    $[52] = intl;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t21) {
    t22 = <span className="uppercase">{t21}</span>;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  const t23 = cart?.totalQuantity ?? 0;
  let t24;
  if ($[56] !== t23) {
    t24 = <span className="text-right">{t23}</span>;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t22 || $[59] !== t24) {
    t25 = <div className="flex items-center justify-between">{t22}{t24}</div>;
    $[58] = t22;
    $[59] = t24;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== intl) {
    t26 = intl.formatMessage({
      defaultMessage: "Shipping",
      description: "Shipping",
      id: "J9R5IK"
    });
    $[61] = intl;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t26) {
    t27 = <span className="uppercase">{t26}</span>;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <span className="text-right">Calculated at checkout</span>;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== t27) {
    t29 = <div className="flex items-center justify-between">{t27}{t28}</div>;
    $[66] = t27;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  let t30;
  if ($[68] !== intl) {
    t30 = intl.formatMessage({
      defaultMessage: "Total Amount",
      description: "Total Amount",
      id: "bc/cr0"
    });
    $[68] = intl;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== t30) {
    t31 = <span className="uppercase">{t30}</span>;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  const t32 = cart?.cost?.totalAmount?.amount;
  const t33 = cart?.cost?.totalAmount?.currencyCode;
  let t34;
  if ($[72] !== lang || $[73] !== t32 || $[74] !== t33) {
    t34 = <Price amount={t32} className="text-right" currencyCode={t33} lang={lang} />;
    $[72] = lang;
    $[73] = t32;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== t31 || $[77] !== t34) {
    t35 = <div className="flex items-center justify-between">{t31}{t34}</div>;
    $[76] = t31;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t25 || $[80] !== t29 || $[81] !== t35) {
    t36 = <section className="grid gap-2 text-sm">{t25}{t29}{t35}</section>;
    $[79] = t25;
    $[80] = t29;
    $[81] = t35;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  const t37 = cart?.checkoutUrl;
  let t38;
  if ($[83] !== intl) {
    t38 = intl.formatMessage({
      defaultMessage: "Proceed to Checkout",
      description: "Proceed to Checkout",
      id: "LC+ESC"
    });
    $[83] = intl;
    $[84] = t38;
  } else {
    t38 = $[84];
  }
  let t39;
  if ($[85] !== handleClickTrack || $[86] !== t37 || $[87] !== t38) {
    t39 = <a href={t37} className="btn btn-base btn-bg btn-primary" onClick={handleClickTrack}>{t38}</a>;
    $[85] = handleClickTrack;
    $[86] = t37;
    $[87] = t38;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== t20 || $[90] !== t36 || $[91] !== t39) {
    t40 = <footer className="grid gap-4 text-sm text-neutral-500">{t20}{t36}{t39}</footer>;
    $[89] = t20;
    $[90] = t36;
    $[91] = t39;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== t14 || $[94] !== t40) {
    t41 = <>{t14}{t40}</>;
    $[93] = t14;
    $[94] = t40;
    $[95] = t41;
  } else {
    t41 = $[95];
  }
  return t41;
}
function _temp(t0) {
  const {
    node: baseCartLineMasked
  } = t0;
  return baseCartLineMasked;
}